import { useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Check, FileArchive, Loader2, X } from "lucide-react";
import * as T from "./api";
import { Overlay, DialogTitle } from "./dialogs";
import ErrorDetailsDialog from "./ErrorDetailsDialog";
import { BTN_ORANGE, BTN_GHOST, MONO } from "./theme";

const STAGES = [
  ["creating", "Creating job"],
  ["folders", "Restoring folders"],
  ["files", "Restoring files"],
  ["takeoffs", "Restoring takeoffs"],
  ["estimate/activity", "Restoring estimate & activity"],
  ["finishing", "Finishing"],
];

const fmtDur = (s) => {
  const h = Math.floor(s / 3600), m = Math.round((s % 3600) / 60);
  return h ? `${h}h ${m}m` : `${m}m`;
};

/**
 * Import Job Backup — restores a complete job ZIP exported by the source
 * program. Flow: pick one ZIP -> stream upload -> validate (no writes) ->
 * preview -> Import as New Job (default; duplicates/incomplete need an
 * explicit opt-in) -> staged restore with rollback on failure.
 * When the backup was already imported, a Repair mode is offered instead:
 * it restores only the MISSING drawings into the existing job and never
 * touches takeoffs, estimate, or labels.
 */
export default function ImportJobDialog({ open, onClose, onImported }) {
  const inputRef = useRef(null);
  const [phase, setPhase] = useState("pick"); // pick|uploading|validating|preview|committing|done|error
  const [pct, setPct] = useState(0);
  const [importId, setImportId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [stage, setStage] = useState(null);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState("");
  const [allowDup, setAllowDup] = useState(false);
  const [allowIncomplete, setAllowIncomplete] = useState(false);
  const [upInfo, setUpInfo] = useState(null);   // {speedBps, etaText, chunk, chunkCount, retries}
  const [file, setFile] = useState(null);       // kept for resume
  const [errMeta, setErrMeta] = useState(null); // feeds the View Error Details window
  const [showDetails, setShowDetails] = useState(false);

  const reset = () => {
    setPhase("pick"); setPct(0); setImportId(null); setPreview(null);
    setStage(null); setSummary(null); setError(""); setAllowDup(false); setAllowIncomplete(false);
    setUpInfo(null); setFile(null); setErrMeta(null); setShowDetails(false);
  };
  const close = () => {
    if (phase === "committing") return; // never interrupt a restore
    if (importId && (phase === "pick" || phase === "preview" || phase === "error")) T.abortImport(importId).catch(() => {});
    reset();
    onClose();
  };

  const startUpload = async (f, resumeId = null) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith(".zip")) {
      toast.error("Import the job backup ZIP itself — don't unzip or rename it.");
      return;
    }
    setFile(f);
    setPhase("uploading"); setPct(0); setError(""); setErrMeta(null); setShowDetails(false);
    let failStage = "upload";
    try {
      const iid = await T.uploadImportZip(f, (info) => { setPct(info.pct); setUpInfo(info); }, resumeId);
      setImportId(iid);
      setPhase("validating");
      failStage = "manifest validation";
      const pv = await T.validateImport(iid);
      setPreview(pv);
      setPhase("preview");
    } catch (e) {
      const m = e.upload; // UploadError from the resumable pump
      const corrId = m?.correlationId || importId || resumeId || null;
      if (m?.correlationId) setImportId(m.correlationId); // keep the session alive for resume
      setErrMeta({
        title: "Job backup import", stage: failStage,
        detail: m ? `Upload stopped at byte ${(m.offset || 0).toLocaleString()} (part ${m.chunk}): ${e.message}` : (e?.response?.data?.detail || e.message || "The backup could not be read."),
        correlationId: corrId, name: f.name, size: f.size,
        received: m?.done, offset: m?.offset, chunk: m?.chunk,
        httpStatus: m?.httpStatus ?? e?.response?.status, retries: m?.retries,
        lastStep: failStage === "upload" ? "chunk upload" : "upload complete",
      });
      setError(e?.response?.data?.detail || e.message || "The backup could not be read.");
      setPhase("error");
    }
  };

  const doCommit = async (repairProjectId = null) => {
    setPhase("committing"); setStage("creating");
    const poll = setInterval(() => {
      T.importStatus(importId).then((s) => { if (s?.stage) setStage(s.stage); }).catch(() => {});
    }, 700);
    try {
      const sum = await T.commitImport(importId, {
        allowDuplicate: allowDup,
        confirmIncomplete: allowIncomplete,
        ...(repairProjectId ? { repairProjectId } : {}),
      });
      clearInterval(poll);
      setSummary(sum);
      setPhase("done");
    } catch (e) {
      clearInterval(poll);
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.duplicate) {
        setAllowDup(false); setPhase("preview");
        toast.error("That exact backup was imported before — tick “import another copy” if you really want a duplicate.");
      } else if (e?.response?.status === 409 && d?.needsIncompleteConfirm) {
        setAllowIncomplete(false); setPhase("preview");
        toast.error("The backup has incomplete files — tick the confirmation to import anyway.");
      } else {
        setError(typeof d === "string" ? d : "Import failed and was fully rolled back.");
        setPhase("error");
      }
    }
  };

  const stageIdx = STAGES.findIndex(([k]) => k === stage);
  const c = preview?.counts || {};

  return (
    <Overlay open={open} onClose={close}>
      <DialogTitle onClose={phase === "committing" ? undefined : close}>Import Job Backup</DialogTitle>

      {phase === "pick" && (
        <div className="space-y-3">
          <p className="text-sm text-slate-600">
            Pick one backup ZIP exported by the source program (project-row download or Job Backup).
            Import the ZIP itself — don't unzip, rename, or edit it.
          </p>
          <button
            onClick={() => inputRef.current?.click()}
            className="flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed border-slate-300 px-4 py-8 text-slate-500 transition-colors hover:border-[#f97316] hover:text-[#f97316]"
            data-testid="import-pick-zone"
          >
            <FileArchive className="h-8 w-8" />
            <span className="text-sm font-semibold">Choose the backup ZIP…</span>
          </button>
          <input
            ref={inputRef} type="file" accept=".zip" className="hidden" data-testid="import-file-input"
            onChange={(e) => { startUpload(e.target.files?.[0]); e.target.value = ""; }}
          />
        </div>
      )}

      {(phase === "uploading" || phase === "validating") && (
        <div className="flex flex-col items-center gap-3 py-8" data-testid="import-progress">
          <Loader2 className="h-8 w-8 animate-spin text-[#f97316]" />
          <p className="text-sm font-semibold text-slate-700">
            {phase === "uploading" ? `Uploading backup… ${pct}%` : "Validating backup…"}
          </p>
          {phase === "uploading" && (
            <>
              <div className="h-2 w-64 overflow-hidden rounded-full bg-slate-200">
                <div className="h-full bg-[#f97316] transition-all" style={{ width: `${pct}%` }} />
              </div>
              {upInfo && (
                <p className={`${MONO} text-[11px] text-slate-500`} data-testid="import-upload-detail">
                  {(upInfo.done / 1048576).toFixed(1)} of {(upInfo.total / 1048576).toFixed(1)} MB
                  {upInfo.speedBps ? ` · ${(upInfo.speedBps / 1048576).toFixed(1)} MB/s` : ""}
                  {upInfo.etaText ? ` · ${upInfo.etaText} left` : ""}
                  {upInfo.chunk ? ` · part ${upInfo.chunk}/${upInfo.chunkCount}` : ""}
                  {upInfo.retries ? ` · retry ${upInfo.retries}` : ""}
                </p>
              )}
            </>
          )}
        </div>
      )}

      {phase === "preview" && preview && (
        <div className="space-y-3" data-testid="import-preview">
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <p className="text-sm font-bold text-slate-900">{preview.name}</p>
            <p className="text-xs text-slate-500">
              {preview.status}{preview.dueDate ? ` · due ${preview.dueDate}` : ""}
              {preview.exportedAt ? ` · exported ${new Date(preview.exportedAt).toLocaleString()}` : ""}
            </p>
          </div>
          <div className="grid grid-cols-4 gap-2 text-center">
            {[["Plans", c.plans], ["Documents", c.documents], ["Folders", c.folders], ["Takeoffs", c.takeoffs],
              ["Highlights", c.highlights], ["Job items", c.jobItems], ["Manual lines", c.manualLines], ["Bookmarks", c.bookmarks]].map(([l, v]) => (
              <div key={l} className="rounded-lg border border-slate-200 p-2">
                <p className={`${MONO} text-sm font-bold text-slate-900`}>{v ?? 0}</p>
                <p className="text-[9px] font-semibold uppercase tracking-wider text-slate-400">{l}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-slate-500">Historical tracked time: <b className="text-slate-800">{fmtDur(preview.totalSeconds || 0)}</b> (imports once as Imported history)</p>

          {preview.warnings?.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800" data-testid="import-warnings">
              {preview.warnings.map((w, i) => <p key={i} className="whitespace-pre-wrap">⚠ {w}</p>)}
            </div>
          )}
          {preview.incomplete?.length > 0 && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-2.5" data-testid="import-incomplete">
              <p className="text-xs font-semibold text-amber-900">This backup is incomplete — {preview.incomplete.length} file(s) were partial or missing at export:</p>
              {preview.incomplete.map((f, i) => <p key={i} className="text-xs text-amber-800">• {f.name} ({f.status})</p>)}
              <label className="mt-2 flex items-center gap-2 text-xs font-semibold text-amber-900">
                <input type="checkbox" checked={allowIncomplete} onChange={(e) => setAllowIncomplete(e.target.checked)} data-testid="import-confirm-incomplete" />
                Import anyway — the missing files stay documented on the job
              </label>
            </div>
          )}
          {preview.duplicate && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-2.5" data-testid="import-duplicate">
              <p className="flex items-center gap-1.5 text-xs font-semibold text-red-800"><AlertTriangle className="h-3.5 w-3.5" /> This exact backup was already imported{preview.duplicate.name ? ` as “${preview.duplicate.name}”` : ""}.</p>
              {preview.duplicate.repairable && (
                <p className="mt-1.5 text-[11px] leading-snug text-red-700" data-testid="import-repair-hint">
                  Drawings missing from that job? Use <b>Restore Missing Drawings</b> — it only adds the files that aren't there; takeoffs, estimate and labels are never touched.
                </p>
              )}
              <label className="mt-2 flex items-center gap-2 text-xs font-semibold text-red-900">
                <input type="checkbox" checked={allowDup} onChange={(e) => setAllowDup(e.target.checked)} data-testid="import-allow-duplicate" />
                Import another copy as a separate new job
              </label>
            </div>
          )}

          <div className="flex flex-wrap justify-end gap-2 pt-1">
            <button className={BTN_GHOST} onClick={close} data-testid="import-cancel">Cancel</button>
            {preview.duplicate?.repairable && (
              <button
                className={BTN_ORANGE}
                disabled={preview.incomplete?.length > 0 && !allowIncomplete}
                onClick={() => doCommit(preview.duplicate.project_id)}
                data-testid="import-repair"
              >
                Restore Missing Drawings
              </button>
            )}
            <button
              className={preview.duplicate?.repairable ? BTN_GHOST : BTN_ORANGE}
              disabled={(!!preview.duplicate && !allowDup) || (preview.incomplete?.length > 0 && !allowIncomplete)}
              onClick={() => doCommit(null)}
              data-testid="import-commit"
            >
              Import as New Job
            </button>
          </div>
        </div>
      )}

      {phase === "committing" && (
        <div className="space-y-2 py-4" data-testid="import-stages">
          <p className="flex items-center gap-2 text-sm font-semibold text-slate-700"><Check className="h-4 w-4 text-emerald-500" /> Validating backup</p>
          {STAGES.map(([k, l], i) => (
            <p key={k} className={`flex items-center gap-2 text-sm ${i < stageIdx ? "text-slate-500" : i === stageIdx ? "font-semibold text-slate-800" : "text-slate-300"}`}>
              {i < stageIdx ? <Check className="h-4 w-4 text-emerald-500" /> : i === stageIdx ? <Loader2 className="h-4 w-4 animate-spin text-[#f97316]" /> : <span className="h-4 w-4" />}
              {l}
            </p>
          ))}
        </div>
      )}

      {phase === "done" && summary && (
        <div className="space-y-3" data-testid="import-done">
          {summary.repair ? (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="import-repair-done">
              <p className="font-semibold">Repair complete on “{summary.name}”.</p>
              <p className="mt-1 text-xs">
                {summary.files_restored} missing drawing(s) restored · {summary.files_skipped_existing} already in place.
                Takeoffs, estimate and labels were not touched.
              </p>
              {summary.files_missing > 0 && (
                <p className="mt-1 text-xs font-semibold text-amber-700" data-testid="import-repair-still-missing">
                  {summary.files_missing} file(s) could not be restored — they were partial or missing in the backup itself.
                </p>
              )}
            </div>
          ) : (
            <p className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm font-semibold text-emerald-800">
              Imported “{summary.name}” as a new job — {summary.counts?.plans ?? 0} plan file(s), {summary.counts?.takeoffs ?? 0} takeoffs, {fmtDur(summary.totalSeconds || 0)} of history.
            </p>
          )}
          <div className="flex justify-end gap-2">
            <button className={BTN_GHOST} onClick={close}>Close</button>
            <button className={BTN_ORANGE} onClick={() => { const pid = summary.project_id; reset(); onClose(); onImported?.(pid); }} data-testid="import-open-job">
              {summary.repair ? "Open Job" : "Open Imported Job"}
            </button>
          </div>
        </div>
      )}

      {phase === "error" && (
        <div className="space-y-3" data-testid="import-error">
          <p className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</p>
          <div className="flex flex-wrap justify-end gap-2">
            {errMeta && (
              <button className={BTN_GHOST} onClick={() => setShowDetails(true)} data-testid="import-error-details">View Error Details</button>
            )}
            {errMeta?.correlationId && file && (
              <button className={BTN_ORANGE} onClick={() => startUpload(file, errMeta.correlationId)} data-testid="import-resume">Resume upload</button>
            )}
            <button className={BTN_GHOST} onClick={close}>Close</button>
            <button className={BTN_ORANGE} onClick={reset} data-testid="import-retry">Try another ZIP</button>
          </div>
        </div>
      )}
      {showDetails && errMeta && (
        <ErrorDetailsDialog
          error={errMeta}
          onClose={() => setShowDetails(false)}
          onRetry={errMeta.correlationId && file ? () => { setShowDetails(false); startUpload(file, errMeta.correlationId); } : null}
        />
      )}
    </Overlay>
  );
}
